/** One aerial photograph out of the flight, with its metadata and the neighbours it was clicked with. */
import { useEffect, useState } from 'react';
import { Anchor, Button, Group, Image, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import type { PhotoProperties } from './photoSource';

export interface PhotoViewerProps {
  photos: PhotoProperties[];
  index: number;
  onIndex: (index: number) => void;
  /** A signed link to the photograph at `path`; only the original exists, previews may come later. */
  imageUrlFor: (path: string) => Promise<string>;
  onClose: () => void;
}

const ROWS = ['name', 'time', 'rel_altitude', 'agl_m', 'gimbal_yaw', 'tilt_deg', 'gps_status', 'geo_error_h_m', 'model', 'size_bytes'] as const;

/** What the link lookup answered, and for which path, so a stale answer never shows under another photo. */
interface Resolved {
  path: string;
  url: string | null;
}

export function PhotoViewer({ photos, index, onIndex, imageUrlFor, onClose }: PhotoViewerProps) {
  const { t } = useTranslation('viewer');
  const { bytes, dateTime, number } = useFormat();
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const photo = photos[index];
  const path = photo?.path;
  const prevPath = photos[index - 1]?.path;
  const nextPath = photos[index + 1]?.path;

  useEffect(() => {
    if (!path) return;
    let alive = true;
    imageUrlFor(path)
      .then((url) => {
        if (alive) setResolved({ path, url });
      })
      .catch(() => {
        if (alive) setResolved({ path, url: null });
      });
    // Prefetch the neighbours so prev/next does not wait on a round trip.
    for (const neighbour of [prevPath, nextPath]) if (neighbour) imageUrlFor(neighbour).catch(() => {});
    return () => {
      alive = false;
    };
  }, [imageUrlFor, path, prevPath, nextPath]);

  const current = resolved && resolved.path === path ? resolved : null;
  const src = current?.url ?? null;
  const failed = current !== null && current.url === null;

  const row = (key: (typeof ROWS)[number]): string => {
    const raw = photo?.[key];
    if (raw === undefined || raw === null || raw === '') return '—';
    switch (key) {
      case 'time':
        return dateTime(String(raw));
      case 'rel_altitude':
      case 'agl_m':
        return `${number(Number(raw), 1)}\u00a0${t('units.m')}`;
      case 'geo_error_h_m':
        return `${number(Number(raw) * 100, 0)}\u00a0${t('units.cm')}`;
      case 'gimbal_yaw':
      case 'tilt_deg':
        return `${number(Number(raw), 0)}°`;
      case 'size_bytes':
        return bytes(Number(raw));
      default:
        return String(raw);
    }
  };

  return (
    <Modal opened onClose={onClose} title={t('photo.title')} size="lg">
      <Stack gap="md">
        {src ? (
          <Image src={src} alt={photo?.name ?? ''} radius="md" fit="contain" mah={420} />
        ) : (
          <Text fz={13} c={brand.textMuted}>
            {failed ? t('photo.unavailable') : t('feature.loading')}
          </Text>
        )}
        <Stack gap={6}>
          {ROWS.map((key) => (
            <Group key={key} justify="space-between" gap="xs" wrap="nowrap">
              <Text fz={12} c={brand.textMuted}>
                {t(`photo.props.${key}`)}
              </Text>
              <Text fz={12} fw={600}>
                {row(key)}
              </Text>
            </Group>
          ))}
        </Stack>
        <Group justify="space-between">
          <Group gap="xs">
            <Button size="xs" variant="default" disabled={index <= 0} onClick={() => onIndex(index - 1)}>
              {t('photo.prev')}
            </Button>
            <Button size="xs" variant="default" disabled={index >= photos.length - 1} onClick={() => onIndex(index + 1)}>
              {t('photo.next')}
            </Button>
            <Text fz={12} c={brand.textMuted}>
              {t('photo.position', { index: index + 1, count: photos.length })}
            </Text>
          </Group>
          {src && (
            <Anchor href={src} target="_blank" rel="noreferrer" fz={13}>
              {t('photo.open')}
            </Anchor>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
